// Supplier invoice and its match (P4.3; 01-SPEC §3 step 9, D59). Pure rules, unit-tested: an
// invoice is checked against the PO (currency, arithmetic, VAT at the PO rate, not above the PO
// total with earlier invoices) and the delivery notes (quantities invoiced to date never above
// what was received in good condition). An invoice without quantities is an advance or milestone
// invoice, checked against the PO's payment schedule. Amounts are integer minor units.

import { z } from 'zod';
import { deliverable } from '../delivery/delivery';
import { poLineTotal, vatOn, type Milestone, type PoLineType } from '../po/po';

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const decimals2 = (v: number) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6;
const money = z
  .number()
  .nonnegative('Cannot be negative')
  .max(10_000_000_000)
  .refine(decimals2, 'Up to 2 decimals');

export const invoiceInputSchema = z.strictObject({
  /** ETag of the PO row the page showed (invoices are recorded one at a time per PO). */
  etag: z.string().min(1).max(200),
  invoiceNo: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(40)
    .regex(/^[\p{L}\p{N} ./_#-]+$/u, 'Letters, digits and . / _ # - only'),
  invoiceDate: z.string().regex(DATE, 'Pick a date'),
  /** Major units, as printed on the invoice. */
  subtotal: money,
  vat: money,
  total: money.refine((v) => v > 0, 'More than 0'),
  /** Quantities invoiced per PO line (months for a monthly line); all 0 for an advance. */
  lines: z
    .array(
      z.strictObject({
        lineNo: z.number().int().min(1).max(500),
        qty: z
          .number()
          .nonnegative()
          .max(1_000_000)
          .refine((v) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-6, 'Up to 3 decimals'),
      }),
    )
    .max(50)
    .default([]),
});
export type InvoiceInput = z.infer<typeof invoiceInputSchema>;

/** Normalised for the duplicate check: "INV-0012" and "inv 0012" are the same invoice. */
export const invoiceKey = (no: string) => no.toUpperCase().replace(/[^\p{L}\p{N}]/gu, '');

export interface MatchPoLine {
  lineNo: number;
  description: string;
  type: PoLineType;
  qty: number;
  months: number;
  /** Minor units (per month for a monthly line). */
  unitPrice: number;
  /** Received to date in good condition (delivery notes). */
  received: number;
}

export interface MatchPo {
  currency: string;
  vatRate: number;
  total: number;
  lines: MatchPoLine[];
  schedule: { milestone: Milestone; amount: number }[];
}

export interface PriorInvoices {
  /** Totals (incl. VAT) of the invoices already recorded on the PO. */
  total: number;
  /** Quantities already invoiced per line. */
  qty: Map<number, number>;
  /** Totals of earlier advance or milestone invoices (no quantities). */
  amountOnly: number;
}

export interface InvoiceFacts {
  currency: string;
  subtotal: number;
  vat: number;
  total: number;
  lines: { lineNo: number; qty: number }[];
}

export const CHECK_IDS = [
  'currency',
  'arithmetic',
  'vat',
  'poTotal',
  'lines',
  'quantities',
  'schedule',
] as const;
export type CheckId = (typeof CHECK_IDS)[number];

export interface MatchCheck {
  id: CheckId;
  ok: boolean;
  /** Values for the message (amounts in minor units as strings, items by name). */
  values: Record<string, string>;
}

export interface MatchResult {
  matched: boolean;
  /** Goods/services invoice (quantities given) or an advance/milestone invoice. */
  kind: 'quantities' | 'amount';
  checks: MatchCheck[];
}

/** One minor unit either way: suppliers round per line or on the total. */
const TOLERANCE = 1;
const round3 = (v: number) => Math.round(v * 1000) / 1000;

/** The amount of `qty` of a PO line (for a monthly line, `qty` is months billed). */
export function lineAmount(line: MatchPoLine, qty: number): number {
  return line.type === 'monthly'
    ? poLineTotal({ type: 'monthly', qty: line.qty, months: 1, unitPrice: line.unitPrice }) *
        Math.round(qty)
    : poLineTotal({ type: line.type, qty, months: 1, unitPrice: line.unitPrice });
}

/** How much of a line may be invoiced in all: received items, or the months ordered. */
const invoiceable = (l: MatchPoLine) => (deliverable(l.type) ? l.received : l.months);

export function matchInvoice(po: MatchPo, prior: PriorInvoices, inv: InvoiceFacts): MatchResult {
  const checks: MatchCheck[] = [];
  const add = (id: CheckId, ok: boolean, values: Record<string, string> = {}) =>
    checks.push({ id, ok, values });

  add('currency', inv.currency === po.currency, { po: po.currency, invoice: inv.currency });
  add('arithmetic', Math.abs(inv.subtotal + inv.vat - inv.total) <= TOLERANCE, {
    sum: String(inv.subtotal + inv.vat),
  });
  const vat = vatOn(inv.subtotal, po.vatRate);
  add('vat', Math.abs(inv.vat - vat) <= TOLERANCE, {
    expected: String(vat),
    rate: String(po.vatRate),
  });
  const after = prior.total + inv.total;
  add('poTotal', after <= po.total + TOLERANCE, {
    over: String(Math.max(0, after - po.total)),
    poTotal: String(po.total),
  });

  const given = inv.lines.filter((l) => l.qty > 0);
  if (given.length > 0) {
    const byNo = new Map(po.lines.map((l) => [l.lineNo, l]));
    let expected = 0;
    const short: string[] = [];
    for (const g of given) {
      const line = byNo.get(g.lineNo);
      if (!line) {
        short.push(`#${g.lineNo}`);
        continue;
      }
      expected += lineAmount(line, g.qty);
      const toDate = round3((prior.qty.get(g.lineNo) ?? 0) + g.qty);
      if (toDate > invoiceable(line)) {
        short.push(
          `${line.description}: ${toDate} invoiced, ${invoiceable(line)} ${deliverable(line.type) ? 'received' : 'months ordered'}`,
        );
      }
    }
    add('lines', Math.abs(inv.subtotal - expected) <= TOLERANCE, { expected: String(expected) });
    add('quantities', short.length === 0, { items: short.join('; ') });
    return { matched: checks.every((c) => c.ok), kind: 'quantities', checks };
  }

  // No quantities: an advance or milestone invoice, within what the schedule allows before delivery.
  const allowed =
    po.schedule
      .filter((s) => s.milestone === 'advance' || s.milestone === 'milestone')
      .reduce((sum, s) => sum + s.amount, 0) - prior.amountOnly;
  add('schedule', allowed > 0 && inv.total <= allowed + TOLERANCE, {
    allowed: String(Math.max(0, allowed)),
  });
  return { matched: checks.every((c) => c.ok), kind: 'amount', checks };
}

export interface InvoiceView {
  invoiceId: string;
  invoiceNo: string;
  invoiceDate: string;
  currency: string;
  subtotal: number;
  vat: number;
  total: number;
  match: MatchResult;
  fileName: string;
  recordedBy: string;
  recordedAt: string;
}

export interface InvoiceLineView extends MatchPoLine {
  unit: string;
  invoicedBefore: number;
}

export interface InvoicePageBody {
  prNo: string;
  poNo: string;
  title: string;
  supplierName: string;
  etag: string;
  po: Omit<MatchPo, 'lines'> & { subtotal: number; vat: number };
  lines: InvoiceLineView[];
  prior: { total: number; amountOnly: number };
  invoices: InvoiceView[];
  canRecord: boolean;
}

/**
 * Invoice amounts typed on the Record invoice screen (D96): the subtotal or the total fills the
 * other two at the PO's VAT rate, in minor units so nothing drifts. VAT typed by hand is left
 * as typed (an invoice may round differently); null when the typed value is not a number.
 */
export function fillInvoiceAmounts(
  changed: 'subtotal' | 'total',
  typed: string,
  vatRatePct: number,
): { subtotal: string; vat: string; total: string } | null {
  const value = Number(typed.trim());
  if (typed.trim() === '' || !Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  const bp = Math.round(vatRatePct * 100);
  const fmt = (m: number) => (m / 100).toFixed(2);
  if (changed === 'subtotal') {
    const vat = Math.round((minor * bp) / 10000);
    return { subtotal: typed, vat: fmt(vat), total: fmt(minor + vat) };
  }
  const sub = Math.round((minor * 10000) / (10000 + bp));
  return { subtotal: fmt(sub), vat: fmt(minor - sub), total: typed };
}
